import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, notification, Row, Select, Space, Typography, Modal, Input, Checkbox, InputNumber, Spin } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CloseOutlined, ContainerOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import request from '../../config/axiosConfig';
import { getQuizInfo, updateQuizQuestions } from '../../services/quiz';
import UploadButton from '../../components/UploadButton';

const { Text, Title } = Typography;

// API helpers (local to this page)
const getAllQuestionsRequest = async () => {
  return (
    await request({
      url: '/question/list',
      method: 'GET',
      params: { page: 1, limit: 10000, status: [1] },
    })
  )?.data;
};

const createQuestionRequest = async (payload) => {
  return (
    await request({
      url: '/question/create',
      method: 'POST',
      data: payload,
    })
  )?.data;
};

// fetch question detail by id
const getQuestionDetailRequest = async (id) => {
  return (
    await request({
      url: '/question/detail',
      method: 'GET',
      params: { id },
    })
  )?.data;
};

const EditQuizQuestions = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const watchQuestionIds = Form.useWatch('question_ids', form);
  const listQuestionRef = useRef(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm] = Form.useForm();
  const editingQuestionIdRef = useRef(null);
  const [editingQuestionType, setEditingQuestionType] = useState(1);
  const [editLoading, setEditLoading] = useState(false);
  // keep local map of newly created questions to display content immediately
  const createdQuestionsRef = useRef({});

  // quiz info (to read current questions and sort place if provided by API)
  const { data: quizInfo } = useQuery({ queryKey: ['quizInfoForQuestions', id], queryFn: () => getQuizInfo(id) });

  // Init form values from quiz info
  useEffect(() => {
    const qs = Array.isArray(quizInfo?.questions) ? quizInfo.questions : [];
    const init = qs.map((q) => ({ question_id: q?.id }));
    form.setFieldsValue({ question_ids: init });
  }, [quizInfo, form]);

  // get data all questions
  const { data: dataAllQuestions } = useQuery({
    queryKey: ['dataAllQuestions'],
    queryFn: () => getAllQuestionsRequest(),
  });

  // update question
  const updateQuestionRequest = async (payload) => {
    return (
      await request({
        url: '/question/update',
        method: 'POST',
        data: payload,
      })
    )?.data;
  };

  const { mutate: mutateUpdateQuestion, isPending: updatingQuestion } = useMutation({
    mutationFn: updateQuestionRequest,
    onSuccess: (res) => {
      if (res?.status === 1) {
        const q = res?.data;
        createdQuestionsRef.current[q.id] = { ...(createdQuestionsRef.current[q.id] || {}), ...q };
        setIsEditModalOpen(false);
        notification.success({ message: 'Cập nhật câu hỏi thành công' });
      } else {
        notification.error({ message: res?.message || 'Không thể cập nhật câu hỏi' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Không thể cập nhật câu hỏi' });
    },
  });

  // convert list questions options
  const dataAllQuestionsOption = useMemo(() => {
    const selected = watchQuestionIds?.map((item) => item?.question_id) || [];
    return dataAllQuestions?.data
      ?.filter((item) => item?.status === 1)
      ?.map((item) => {
        const label = item?.title || item?.content || item?.name || `Câu hỏi #${item?.id}`;
        return { label, value: item?.id, disabled: selected?.includes(item?.id) };
      });
  }, [dataAllQuestions, watchQuestionIds]);

  // handle submit form
  const { mutate: mutateUpdateQuestions, isPending } = useMutation({
    mutationFn: updateQuizQuestions,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Thêm mới câu hỏi thành công!',
        });
        navigate(-1);
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...',
      });
    },
  });

  // create question
  const { mutate: mutateCreateQuestion, isPending: creatingQuestion } = useMutation({
    mutationFn: createQuestionRequest,
    onSuccess: (res) => {
      if (res?.status === 1) {
        const q = res?.data;
        // add to local created map for display content
        createdQuestionsRef.current[q.id] = q;
        // prepend to form list
        const current = form.getFieldValue('question_ids') || [];
        form.setFieldsValue({ question_ids: [{ question_id: q.id }, ...current] });
        setIsCreateModalOpen(false);
        createForm.resetFields();
        notification.success({ message: 'Tạo câu hỏi thành công' });
      } else {
        notification.error({ message: res?.message || 'Không thể tạo câu hỏi' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Không thể tạo câu hỏi' });
    },
  });

  const handleSubmitForm = (data) => {
    const dataSubmit = {
      id,
      question_ids: data?.question_ids ? data?.question_ids?.map((item) => item?.question_id) : [],
    };
    mutateUpdateQuestions(dataSubmit);
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    if (source.index === destination.index) return;

    const current = form.getFieldValue('question_ids');
    const reordered = reorder(current, source.index, destination.index);
    form.setFieldsValue({ question_ids: reordered });
  };

  return (
    <Form
      name="manage-questions"
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={handleSubmitForm}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Danh sách câu hỏi</Title>
        </Col>

        {/* Create Question Modal */}
        <Modal
          title="Thêm câu hỏi"
          width="50%"
          open={isCreateModalOpen}
          onCancel={() => setIsCreateModalOpen(false)}
          okText="Tạo"
          cancelText="Hủy"
          confirmLoading={creatingQuestion}
          onOk={() => {
            createForm
              .validateFields()
              .then((values) => {
                const { question_type, content, content_image, point, order, solution, solution_image, answers, answers_input } = values;
                const payload = {
                  quiz_id: id,
                  question_type,
                  content,
                  content_image,
                  solution,
                  solution_image,
                  point: point ?? 0,
                  order: order ?? 0,
                };
                if (question_type === 1 || question_type === 2) {
                  payload.answers = (answers || []).map((a) => ({
                    content: a?.content,
                    is_correct: a?.is_correct ? 1 : 0,
                  }));
                }
                if (question_type === 3) {
                  const list = Array.isArray(answers_input) ? answers_input : [];
                  if (list.length < 1) {
                    notification.error({ message: 'Cần ít nhất 1 đáp án' });
                    return;
                  }
                  const mapped = list.map((a) => ({
                    content: JSON.stringify({ title: a?.title || '', answer: a?.answer || '' }),
                    is_correct: 1,
                  }));
                  // Validate required title/answer
                  if (mapped.some((m) => {
                    try { const o = JSON.parse(m.content); return !o.title || !o.answer; } catch { return true; }
                  })) {
                    notification.error({ message: 'Tiêu đề và đáp án là bắt buộc' });
                    return;
                  }
                  payload.answers = mapped;
                }
                // validations for answers
                if ((question_type === 1 || question_type === 2)) {
                  const list = payload.answers || [];
                  if (list.length < 2) {
                    notification.error({ message: 'Cần ít nhất 2 đáp án' });
                    return;
                  }
                  const correct = list.filter((a) => a.is_correct === 1).length;
                  if (question_type === 1 && correct !== 1) {
                    notification.error({ message: 'Loại chọn 1 đáp án phải có đúng 1 đáp án đúng' });
                    return;
                  }
                  if (question_type === 2 && correct < 1) {
                    notification.error({ message: 'Loại chọn nhiều đáp án phải có ít nhất 1 đáp án đúng' });
                    return;
                  }
                }
                mutateCreateQuestion(payload);
              })
              .catch(() => {});
          }}
        >
          <Form form={createForm} layout="vertical" initialValues={{ question_type: 1, point: 1, order: 0 }}>
            <Form.Item name="question_type" label="Loại câu hỏi" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 1, label: 'Chọn 1 đáp án đúng' },
                  { value: 2, label: 'Chọn nhiều đáp án đúng' },
                  { value: 3, label: 'Nhập câu trả lời' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="content"
              label="Nội dung câu hỏi"
              rules={[
                {
                  validator: (_, value) => {
                    const img = createForm.getFieldValue('content_image');
                    if ((value && String(value).trim()) || (img && String(img).trim())) return Promise.resolve();
                    return Promise.reject(new Error('Cần có Nội dung câu hỏi hoặc Ảnh nội dung'));
                  },
                },
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
            <Form.Item
              name="content_image"
              label="Ảnh nội dung"
              rules={[
                {
                  validator: (_, value) => {
                    const text = createForm.getFieldValue('content');
                    if ((text && String(text).trim()) || (value && String(value).trim())) return Promise.resolve();
                    return Promise.reject(new Error('Cần có Nội dung câu hỏi hoặc Ảnh nội dung'));
                  },
                },
              ]}
            >
              <UploadButton key="create-content_image" name="content_image" />
            </Form.Item>
            <Form.Item name="solution" label="Hướng dẫn giải">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item name="solution_image" label="Ảnh hướng dẫn">
              <UploadButton key="create-solution_image" name="solution_image" />
            </Form.Item>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="point"
                  label="Điểm"
                  rules={[
                    { required: true, message: 'Vui lòng nhập điểm' },
                    {
                      validator: (_, value) => {
                        if (value === undefined || value === null) return Promise.reject(new Error('Vui lòng nhập điểm'));
                        if (Number(value) <= 0) return Promise.reject(new Error('Điểm phải > 0'));
                        if (Math.round(Number(value) * 100) !== Number(value) * 100) return Promise.reject(new Error('Tối đa 2 chữ số thập phân'));
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber min={0.01} step={0.01} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="order" label="Thứ tự">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            {/* Answers only for type 1 and 2 */}
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.question_type !== cur.question_type}>
              {({ getFieldValue }) => {
                const type = getFieldValue('question_type');
                if (type === 1 || type === 2) {
                  return (
                    <Form.List name="answers">
                      {(fields, { add, remove }) => (
                        <>
                          <div className="flex justify-between items-center" style={{ marginBottom: 15 }}>
                            <Text strong>Đáp án</Text>
                            <Button onClick={() => add()} size="small">Thêm đáp án</Button>
                          </div>
                          {fields.map((f) => (
                            <Row key={f.key} gutter={8} align="middle" justify="center" className="mb-2">
                              <Col flex="auto">
                                <Form.Item name={[f.name, 'content']} rules={[{ required: true, message: 'Nhập nội dung đáp án' }]}>
                                  <Input placeholder="Nội dung đáp án" />
                                </Form.Item>
                              </Col>
                              <Col>
                                <Form.Item name={[f.name, 'is_correct']} valuePropName="checked" initialValue={false} style={{ marginBottom: 20 }}>
                                  <Checkbox>Đúng</Checkbox>
                                </Form.Item>
                              </Col>
                              <Col style={{ display: 'flex', alignItems: 'center' }}>
                                <Button style={{ marginBottom: 20 }} danger type="text" onClick={() => remove(f.name)}>
                                  <DeleteOutlined />
                                </Button>
                              </Col>
                            </Row>
                          ))}
                        </>
                      )}
                    </Form.List>
                  );
                }
                if (type === 3) {
                  return (
                    <Form.List name="answers_input">
                      {(fields, { add, remove }) => (
                        <>
                          <div className="flex justify-between items-center" style={{ marginBottom: 15 }}>
                            <Text strong>Đáp án</Text>
                            <Button onClick={() => add()} size="small">Thêm đáp án</Button>
                          </div>
                          {fields.map((f) => (
                            <Row key={f.key} gutter={8} align="middle" wrap={false} className="mb-2">
                              <Col flex="1">
                                <Form.Item name={[f.name, 'title']} rules={[{ required: true, message: 'Nhập tiêu đề' }]}> 
                                  <Input placeholder="Tiêu đề" />
                                </Form.Item>
                              </Col>
                              <Col flex="1">
                                <Form.Item name={[f.name, 'answer']} rules={[{ required: true, message: 'Nhập đáp án' }]}> 
                                  <Input placeholder="Đáp án" />
                                </Form.Item>
                              </Col>
                              <Col flex="40px" className="flex items-center justify-center">
                                <Button danger type="text" style={{ marginBottom: 20 }} onClick={() => remove(f.name)}>
                                  <DeleteOutlined />
                                </Button>
                              </Col>
                            </Row>
                          ))}
                        </>
                      )}
                    </Form.List>
                  );
                }
                return null;
              }}
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit Question Modal */}
        <Modal
          title="Sửa câu hỏi"
          open={isEditModalOpen}
          onCancel={() => setIsEditModalOpen(false)}
          okText="Cập nhật"
          cancelText="Hủy"
          confirmLoading={updatingQuestion}
          onOk={() => {
            editForm
              .validateFields()
              .then((values) => {
                const { question_type, content, content_image, solution, solution_image, point, order, answers, answers_input } = values;
                // validations for answers when needed
                const qType = Number(question_type ?? editingQuestionType);
                if (qType === 1 || qType === 2) {
                  const list = Array.isArray(answers) ? answers : [];
                  if (list.length < 2) {
                    notification.error({ message: 'Cần ít nhất 2 đáp án' });
                    return;
                  }
                  const correct = list.filter((a) => a?.is_correct).length;
                  if (qType === 1 && correct !== 1) {
                    notification.error({ message: 'Loại chọn 1 đáp án phải có đúng 1 đáp án đúng' });
                    return;
                  }
                  if (qType === 2 && correct < 1) {
                    notification.error({ message: 'Loại chọn nhiều đáp án phải có ít nhất 1 đáp án đúng' });
                    return;
                  }
                }
                const payload = {
                  id: editingQuestionIdRef.current,
                  question_type: qType,
                  content,
                  content_image,
                  solution,
                  solution_image,
                  point: point ?? 0,
                  order: order ?? 0,
                };
                if (qType === 1 || qType === 2) {
                  payload.answers = (answers || []).map((a) => ({ content: a?.content, is_correct: a?.is_correct ? 1 : 0 }));
                }
                if (qType === 3) {
                  const list = Array.isArray(answers_input) ? answers_input : [];
                  if (list.length < 1) {
                    notification.error({ message: 'Cần ít nhất 1 đáp án' });
                    return;
                  }
                  const mapped = list.map((a) => ({
                    content: JSON.stringify({ title: a?.title || '', answer: a?.answer || '' }),
                    is_correct: 1,
                  }));
                  if (mapped.some((m) => {
                    try { const o = JSON.parse(m.content); return !o.title || !o.answer; } catch { return true; }
                  })) {
                    notification.error({ message: 'Tiêu đề và đáp án là bắt buộc' });
                    return;
                  }
                  payload.answers = mapped;
                }
                mutateUpdateQuestion(payload);
              })
              .catch(() => {});
          }}
        >
          <Spin spinning={editLoading}>
            <Form form={editForm} layout="vertical">
              <Form.Item name="question_type" label="Loại câu hỏi" rules={[{ required: true, message: 'Chọn loại câu hỏi' }]}>
                <Select
                  options={[
                    { value: 1, label: 'Chọn 1 đáp án đúng' },
                    { value: 2, label: 'Chọn nhiều đáp án đúng' },
                    { value: 3, label: 'Nhập câu trả lời' },
                  ]}
                  onChange={(val) => {
                    setEditingQuestionType(val);
                    if (val === 3) {
                      editForm.setFieldsValue({ answers: [], answers_input: [] });
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                name="content"
                label="Nội dung câu hỏi"
                rules={[
                  {
                    validator: (_, value) => {
                      const img = editForm.getFieldValue('content_image');
                      if ((value && String(value).trim()) || (img && String(img).trim())) return Promise.resolve();
                      return Promise.reject(new Error('Cần có Nội dung câu hỏi hoặc Ảnh nội dung'));
                    },
                  },
                ]}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
              <Form.Item
                name="content_image"
                label="Ảnh nội dung"
                rules={[
                  {
                    validator: (_, value) => {
                      const text = editForm.getFieldValue('content');
                      if ((text && String(text).trim()) || (value && String(value).trim())) return Promise.resolve();
                      return Promise.reject(new Error('Cần có Nội dung câu hỏi hoặc Ảnh nội dung'));
                    },
                  },
                ]}
              >
                <UploadButton key={`edit-content_image-${editingQuestionIdRef.current || ''}`} name="content_image" />
              </Form.Item>
              <Form.Item name="solution" label="Hướng dẫn giải">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="solution_image" label="Ảnh hướng dẫn">
                <UploadButton key={`edit-solution_image-${editingQuestionIdRef.current || ''}`} name="solution_image" />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    name="point"
                    label="Điểm"
                    rules={[
                      { required: true, message: 'Vui lòng nhập điểm' },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null) return Promise.reject(new Error('Vui lòng nhập điểm'));
                          if (Number(value) <= 0) return Promise.reject(new Error('Điểm phải > 0'));
                          if (Math.round(Number(value) * 100) !== Number(value) * 100) return Promise.reject(new Error('Tối đa 2 chữ số thập phân'));
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber min={0.01} step={0.01} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="order" label="Thứ tự">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            {editingQuestionType === 1 || editingQuestionType === 2 ? (
              <Form.List name="answers">
                {(fields, { add, remove }) => (
                  <>
                    <div className="flex justify-between items-center" style={{ marginBottom: 10 }}>
                      <Text strong>Đáp án</Text>
                      <Button onClick={() => add()} size="small">Thêm đáp án</Button>
                    </div>
                    {fields.map((f) => (
                      <Row key={f.key} gutter={8} align="middle" justify="center" className="mb-2">
                        <Col flex="auto">
                          <Form.Item name={[f.name, 'content']} rules={[{ required: true, message: 'Nhập nội dung đáp án' }]}>
                            <Input placeholder="Nội dung đáp án" />
                          </Form.Item>
                        </Col>
                        <Col>
                          <Form.Item name={[f.name, 'is_correct']} valuePropName="checked" initialValue={false} style={{ marginBottom: 20 }}>
                            <Checkbox>Đúng</Checkbox>
                          </Form.Item>
                        </Col>
                        <Col style={{ display: 'flex', alignItems: 'center' }}>
                          <Button style={{ marginBottom: 20 }} danger type="text" onClick={() => remove(f.name)}>
                            <DeleteOutlined />
                          </Button>
                        </Col>
                      </Row>
                    ))}
                  </>
                )}
              </Form.List>
            ) : null}
            {editingQuestionType === 3 ? (
              <Form.List name="answers_input">
                {(fields, { add, remove }) => (
                  <>
                    <div className="flex justify-between items-center" style={{ marginBottom: 10 }}>
                      <Text strong>Đáp án</Text>
                      <Button onClick={() => add()} size="small">Thêm đáp án</Button>
                    </div>
                    {fields.map((f) => (
                      <Row key={f.key} gutter={8} align="middle" wrap={false} className="mb-2">
                        <Col flex="1">
                          <Form.Item name={[f.name, 'title']} rules={[{ required: true, message: 'Nhập tiêu đề' }]}>
                            <Input placeholder="Tiêu đề" />
                          </Form.Item>
                        </Col>
                        <Col flex="1">
                          <Form.Item name={[f.name, 'answer']} rules={[{ required: true, message: 'Nhập đáp án' }]}>
                            <Input placeholder="Đáp án" />
                          </Form.Item>
                        </Col>
                        <Col flex="40px" className="flex items-center justify-center">
                          <Button danger type="text" style={{ marginBottom: 20 }} onClick={() => remove(f.name)}>
                            <DeleteOutlined />
                          </Button>
                        </Col>
                      </Row>
                    ))}
                  </>
                )}
              </Form.List>
            ) : null}
            </Form>
          </Spin>
        </Modal>

        <Col xs={24}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable-selects">
              {(provided) => (
                <Form.List name="question_ids">
                  {(fields, { add, remove }) => (
                    <>
                      <div className="flex flex-row justify-between items-center">
                        <Text className="mb-4"></Text>
                        <Form.Item>
                          <Space>
                            <Button
                              type="primary"
                              onClick={() => {
                                setIsCreateModalOpen(true);
                              }}
                            >
                              Thêm câu hỏi
                            </Button>
                          </Space>
                        </Form.Item>
                      </div>
                      <div
                        ref={(el) => {
                          provided.innerRef(el);
                          listQuestionRef.current = el;
                        }}
                        {...provided.droppableProps}
                        className={`overflow-y-auto p-2 ${
                          fields?.length > 0 ? 'border border-[#d9d9d9] rounded-lg mb-4' : ''
                        }`}
                      >
                        <>
                          {fields.map((field, index) => (
                            <Draggable key={field.key.toString()} draggableId={field.key.toString()} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    background: snapshot.isDragging ? '#f0f5ff' : '#fff',
                                    padding: 16,
                                    marginBottom: 8,
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'between',
                                    gap: 8,
                                    ...provided.draggableProps.style,
                                    cursor: 'grab',
                                  }}
                                >
                                  {/* Keep the value in form but show content instead of Select */}
                                  <div className="flex-1 min-w-0 mb-0">
                                    <Form.Item
                                      name={[field.name, 'question_id']}
                                      fieldKey={[field.fieldKey, 'question_id']}
                                      hidden
                                      rules={[{ required: true, message: 'Thiếu câu hỏi' }]}
                                    >
                                      <input />
                                    </Form.Item>
                                    {(() => {
                                      const qid = form.getFieldValue(['question_ids', index, 'question_id']);
                                      const qLocal = createdQuestionsRef.current[qid];
                                      const q = qLocal || quizInfo?.questions?.find((x) => x?.id === qid);
                                      const hasText = !!(q?.content && String(q.content).trim());
                                      const hasImage = !!(q?.content_image && String(q.content_image).trim());
                                      const answers = Array.isArray(q?.answers) ? q.answers : [];
                                      const qType = Number(q?.question_type);

                                      return (
                                        <div className="text-[14px] leading-5 text-[#1d1f2c] break-words">
                                          {hasText && (
                                            <div className="whitespace-pre-wrap">{q.content}</div>
                                          )}
                                          {hasImage && (
                                            <div className="mt-2">
                                              <img
                                                src={q.content_image}
                                                alt={`question-${qid}`}
                                                style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 6 }}
                                              />
                                            </div>
                                          )}
                                          {!hasText && !hasImage && (
                                            <div>{`Câu hỏi #${qid || ''}`}</div>
                                          )}
                                          {(qType === 1 || qType === 2) && answers.length > 0 && (
                                            <div className="mt-3">
                                              <div className="text-xs text-gray-500 mb-1">Đáp án:</div>
                                              <ul className="list-none pl-0">
                                                {answers.map((a, i) => {
                                                  const isCorrect = Number(a?.is_correct) === 1 || a?.is_correct === true;
                                                  return (
                                                    <li key={i} className="whitespace-pre-wrap">
                                                      <span className={isCorrect ? 'font-semibold text-green-600' : ''}>
                                                        {a?.label ? `${String(a.label).toUpperCase()}. ` : ''}{a?.content || '(trống)'} {isCorrect ? '✔' : ''}
                                                      </span>
                                                    </li>
                                                  );
                                                })}
                                              </ul>
                                            </div>
                                          )}
                                          {qType === 3 && Array.isArray(answers) && answers.length > 0 && (
                                            <div className="mt-3">
                                              <div className="text-xs text-gray-500 mb-1">Đáp án (nhập):</div>
                                              <ul className="list-none pl-0">
                                                {answers.map((a, i) => {
                                                  let title = '';
                                                  let answer = '';
                                                  try {
                                                    const o = JSON.parse(a?.content || '{}');
                                                    title = o?.title || '';
                                                    answer = o?.answer || '';
                                                  } catch {}
                                                  return (
                                                    <li key={i} className="whitespace-pre-wrap">
                                                      <span className={'font-normal'}>
                                                        {title ? (<><strong>{title}:</strong> </>) : null}{answer || '(trống)'}
                                                      </span>
                                                    </li>
                                                  );
                                                })}
                                              </ul>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>

                                  <Space>
                                    <Button
                                      type="text"
                                      icon={<EditOutlined className="text-xl" />}
                                      onClick={() => {
                                        const qid = form.getFieldValue(['question_ids', index, 'question_id']);
                                        editingQuestionIdRef.current = qid;
                                        // prefer locally created/updated
                                        const qLocal = createdQuestionsRef.current[qid];
                                        const q = qLocal || quizInfo?.questions?.find((x) => x?.id === qid) || {};
                                        setEditingQuestionType(q?.question_type || 1);
                                        if (Number(q?.question_type) === 3) {
                                          const answers_input = Array.isArray(q?.answers)
                                            ? q.answers.map((a) => {
                                                try {
                                                  const o = JSON.parse(a?.content || '{}');
                                                  return { title: o?.title || '', answer: o?.answer || '' };
                                                } catch {
                                                  return { title: '', answer: '' };
                                                }
                                              })
                                            : [];
                                          editForm.setFieldsValue({
                                            question_type: q?.question_type || 3,
                                            content: q?.content || '',
                                            content_image: q?.content_image || '',
                                            point: q?.point ?? 0,
                                            order: q?.order ?? 0,
                                            answers: [],
                                            answers_input,
                                          });
                                        } else {
                                          editForm.setFieldsValue({
                                            question_type: q?.question_type || 1,
                                            content: q?.content || '',
                                            content_image: q?.content_image || '',
                                            point: q?.point ?? 0,
                                            order: q?.order ?? 0,
                                            answers: Array.isArray(q?.answers)
                                              ? q.answers.map((a) => ({ content: a?.content || '', is_correct: Number(a?.is_correct) === 1 }))
                                              : [],
                                            answers_input: [],
                                          });
                                        }
                                        setIsEditModalOpen(true);
                                        // Load full detail (answers, latest fields)
                                        setEditLoading(true);
                                        getQuestionDetailRequest(qid)
                                          .then((res) => {
                                            if (res?.status === 1 && res?.data) {
                                              const d = res.data || {};
                                              setEditingQuestionType(d?.question_type || 1);
                                              if (Number(d?.question_type) === 3) {
                                                const answers_input = Array.isArray(d?.answers)
                                                  ? d.answers.map((a) => {
                                                      try {
                                                        const o = JSON.parse(a?.content || '{}');
                                                        return { title: o?.title || '', answer: o?.answer || '' };
                                                      } catch {
                                                        return { title: '', answer: '' };
                                                      }
                                                    })
                                                  : [];
                                                editForm.setFieldsValue({
                                                  question_type: d?.question_type || 3,
                                                  content: d?.content || '',
                                                  content_image: d?.content_image || '',
                                                  solution: d?.solution || '',
                                                  solution_image: d?.solution_image || '',
                                                  point: d?.point ?? 0,
                                                  order: d?.order ?? 0,
                                                  answers: [],
                                                  answers_input,
                                                });
                                              } else {
                                                editForm.setFieldsValue({
                                                  question_type: d?.question_type || 1,
                                                  content: d?.content || '',
                                                  content_image: d?.content_image || '',
                                                  solution: d?.solution || '',
                                                  solution_image: d?.solution_image || '',
                                                  point: d?.point ?? 0,
                                                  order: d?.order ?? 0,
                                                  answers: Array.isArray(d?.answers)
                                                    ? d.answers.map((a) => ({ content: a?.content || '', is_correct: Number(a?.is_correct) === 1 }))
                                                    : [],
                                                  answers_input: [],
                                                });
                                              }
                                            } else {
                                              notification.error({ message: res?.message || 'Không tải được chi tiết câu hỏi' });
                                            }
                                          })
                                          .catch((e) => {
                                            notification.error({ message: e?.data?.message || 'Không tải được chi tiết câu hỏi' });
                                          })
                                          .finally(() => setEditLoading(false));
                                      }}
                                    />
                                    <Button type="text" danger onClick={() => remove(index)}>
                                      <DeleteOutlined className="text-xl" />
                                    </Button>
                                  </Space>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </>
                      </div>
                    </>
                  )}
                </Form.List>
              )}
            </Droppable>
          </DragDropContext>
        </Col>
        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default EditQuizQuestions;
